// A purchase the server completed while the browser was not there to see it.
//
// The order is made by Stripe's webhook, whatever the page does; the cart is
// emptied, the promo code cleared and the thank-you shown only when the
// browser comes back from the payment (aldoo-ra Store onPaymentSuccess). A
// buyer who paid and closed the tab during the bank's redirect left a cart
// that still holds what they bought, with its code and its payment.
//
// On load, a cart that carries a payment (paymentInfo.paymentIntentId) asks
// the server whether that payment became an order; if it did, the purchase is
// finished here as it would have been: the thank-you dialog, then an empty
// cart and no code. A payment never made (an abandoned checkout) answers no,
// and nothing changes.
import { useEffect } from "react"
import { store } from "aldoo-ra/Store"
import { PaymentStatus } from "../storeApi"

// the webhook may still be on it when the page opens: asked again a little later
const ASK_AT = [0, 4000, 10000]
// the store instance appears as AldooStore first renders (it reads the
// persisted cart then): waited for, briefly
const READY_TRIES = 20
const READY_EVERY = 250
const storeReady = () => !!globalThis[Symbol.for("aldoo.store.instance")]

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

export default function PurchaseRecovery() {
  useEffect(() => {
    let alive = true
    ;(async () => {
      for (let i = 0; i < READY_TRIES && alive && !storeReady(); i++) await wait(READY_EVERY)
      if (!alive || !storeReady()) return
      const paymentIntentId = store.paymentInfo?.paymentIntentId
      // nothing paid for in this browser, or the purchase already finished here
      if (!alive || !paymentIntentId || store.paymentReceipt || !store.cart?.length) return
      for (const delay of ASK_AT) {
        await wait(delay)
        if (!alive) return
        // the buyer paid meanwhile in this page, or started over with another payment
        if (store.paymentReceipt || store.paymentInfo?.paymentIntentId !== paymentIntentId) return
        try {
          const status = await PaymentStatus(paymentIntentId)
          if (!status?.processed) continue
        } catch {
          continue
        }
        if (!alive) return
        // as the return from the payment would have: the thank-you (SuccessStep
        // signs the buyer in and follows the order), the cart and the code cleared
        store.onPaymentSuccess({ paymentIntentId })
        return
      }
    })()
    return () => {
      alive = false
    }
  }, [])
  return null
}
